import React from 'react';
import {useSelector} from 'react-redux';
import {selectTutorialReplayMeta, selectTutorialState} from '../../../redux/reducers/privateStateReducer/selectors';
import {getTutorialChapter} from '../../../configs/tutorial';
import {formatTutorialStepNumber, getTutorialModalKey} from '../../../utils/tutorial/tutorialAnalyticsIds';
import './TutorialReplayStatusPanel.css';

/**
 * Где в главе игрок в этот момент записи прохождения обучения (просмотр
 * админом): глава, шаг «N из M» с id шага, задание шага и открытая модалка.
 *
 * Шаг берется из самой записи (tutorialMeta у экшенов сценария): конфиги глав
 * с тех пор могли поменяться, и номер шага из старой записи указывал бы на
 * другой шаг. Текст задания — из текущего конфига и только если шаг с этим
 * номером в нем тот же самый.
 *
 * Обучающие модалки при просмотре не показываются: их хост на завершении
 * главы сохранил бы прохождение за зрителя.
 */
const TutorialReplayStatusPanel = () => {
  const tutorialState = useSelector(selectTutorialState);
  const replayMeta = useSelector(selectTutorialReplayMeta);

  const chapterId = replayMeta?.chapterId || tutorialState?.chapterId;
  if (!chapterId) {
    return null;
  }

  const chapter = getTutorialChapter(chapterId);
  const stepIndex = replayMeta?.stepIndex ?? tutorialState.stepIndex;
  const stepsCount = replayMeta?.stepsCount ?? chapter?.steps?.length ?? null;
  const stepId = replayMeta ? replayMeta.stepId : (chapter?.steps?.[stepIndex]?.id ?? null);
  const activeModalId = replayMeta ? replayMeta.activeModalId : tutorialState.activeModalId;
  const isChapterCompleted = replayMeta ? replayMeta.isChapterCompleted : tutorialState.isChapterCompleted;

  const currentConfigStep = chapter?.steps?.[stepIndex];
  const isSameStepInConfig = Boolean(currentConfigStep) && currentConfigStep.id === stepId;
  const taskText = isSameStepInConfig ? (currentConfigStep.task?.text || null) : null;
  const modalKey = activeModalId ? (getTutorialModalKey(chapterId, activeModalId) || activeModalId) : null;

  return (
    <div className={'tutorial-replay-status-panel'} data-testid={'tutorial-replay-status-panel'}>
      <div className={'tutorial-replay-status-panel_title'}>
        Запись обучения · {chapter?.shortTitle || chapterId}
      </div>
      {isChapterCompleted ? (
        <div className={'tutorial-replay-status-panel_row'}>Глава пройдена</div>
      ) : (
        <div className={'tutorial-replay-status-panel_row'}>
          Шаг {Math.min(stepIndex + 1, stepsCount ?? stepIndex + 1)}{stepsCount ? ` из ${stepsCount}` : ''}
          {stepId && (
            <span className={'tutorial-replay-status-panel_id'}>
              {' '}{formatTutorialStepNumber(stepIndex)}_{stepId}
            </span>
          )}
        </div>
      )}
      {!isChapterCompleted && taskText && (
        <div className={'tutorial-replay-status-panel_row tutorial-replay-status-panel_task'}>{taskText}</div>
      )}
      {modalKey && (
        <div className={'tutorial-replay-status-panel_row'}>
          Модалка: <span className={'tutorial-replay-status-panel_id'}>{modalKey}</span>
        </div>
      )}
    </div>
  );
};

export default TutorialReplayStatusPanel;
